// AGC: CustomMaintenanceModal - Popup for configuring custom maintenance alarms
// AGC: Allows user to set hours and select maintenance types

import React, { useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import {
  X,
  Clock,
  Wrench,
  Settings,
  Check,
  Droplets,
  Circle,
  Cog,
  AlertTriangle,
  Filter,
} from 'lucide-react'

// AGC: Available maintenance types for custom alarms
export interface MaintenanceTypeOption {
  id: string
  labelKey: string
  icon: React.ReactNode
}

// AGC: Custom maintenance configuration
export interface CustomMaintenanceConfig {
  hours: number
  maintenanceTypes: string[]
  description?: string
}

interface CustomMaintenanceModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (config: CustomMaintenanceConfig) => void
  currentHours?: number
  initialHours?: number
  initialTypes?: string[]
  isSaving?: boolean
}

// AGC: Available maintenance types
const MAINTENANCE_TYPES: MaintenanceTypeOption[] = [
  { id: 'filter', labelKey: 'machines.detail.customMaintenance.types.filter', icon: <Filter className="w-5 h-5" /> },
  { id: 'oil_change', labelKey: 'machines.detail.customMaintenance.types.oilChange', icon: <Droplets className="w-5 h-5" /> },
  { id: 'tire', labelKey: 'machines.detail.customMaintenance.types.tire', icon: <Circle className="w-5 h-5" /> },
  { id: 'belt', labelKey: 'machines.detail.customMaintenance.types.belt', icon: <Cog className="w-5 h-5" /> },
  { id: 'preventive', labelKey: 'machines.detail.customMaintenance.types.preventive', icon: <Settings className="w-5 h-5" /> },
  { id: 'corrective', labelKey: 'machines.detail.customMaintenance.types.corrective', icon: <Wrench className="w-5 h-5" /> },
  { id: 'other', labelKey: 'machines.detail.customMaintenance.types.other', icon: <AlertTriangle className="w-5 h-5" /> },
]

/**
 * AGC: CustomMaintenanceModal - Modal for configuring custom maintenance alarms
 * AGC: Features:
 * AGC: - Input for hours interval
 * AGC: - Checkboxes for maintenance types
 * AGC: - Save configuration for custom alarm
 */
export const CustomMaintenanceModal: React.FC<CustomMaintenanceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentHours = 0,
  initialHours = 250,
  initialTypes = [],
  isSaving = false,
}) => {
  const { t } = useTranslation()
  const [hours, setHours] = useState<number>(initialHours)
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialTypes)
  const [description, setDescription] = useState('')

  // AGC: Toggle maintenance type selection
  const toggleType = (typeId: string) => {
    setSelectedTypes(prev => 
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    )
  }

  // AGC: Handle save
  const handleSave = () => {
    if (hours <= 0) return
    
    onSave({
      hours,
      maintenanceTypes: selectedTypes,
      description: description.trim() || undefined,
    })
  }

  // AGC: Calculate when the alarm will trigger
  const alarmAtHours = currentHours + hours

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* AGC: Header */}
        <div className="px-4 py-3 bg-emerald-600 dark:bg-emerald-700 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Settings className="w-5 h-5" />
            <h2 className="text-lg font-semibold">
              {t('machines.detail.customMaintenance.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Content */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* AGC: Hours Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t('machines.detail.customMaintenance.intervalLabel')}
              </div>
            </label>
            <div className="relative">
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                min={1}
                step={10}
                className="w-full px-4 py-3 pr-12 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="250"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                {t('machines.detail.customMaintenance.hours')}
              </span>
            </div>
            {/* AGC: Show when alarm will trigger */}
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              {t('machines.detail.customMaintenance.alarmWillTriggerAt', { hours: alarmAtHours.toFixed(0) })}
            </p>
          </div>

          {/* AGC: Maintenance Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                {t('machines.detail.customMaintenance.typesLabel')}
              </div>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('machines.detail.customMaintenance.typesHelp')}
            </p>
            
            <div className="space-y-2">
              {MAINTENANCE_TYPES.map((type) => {
                const isSelected = selectedTypes.includes(type.id)
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleType(type.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    {/* AGC: Checkbox */}
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    
                    {/* AGC: Icon */}
                    <span className={isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}>
                      {type.icon}
                    </span>
                    
                    {/* AGC: Label */}
                    <span className="font-medium">
                      {t(type.labelKey)}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* AGC: Description (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('machines.detail.customMaintenance.descriptionLabel')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              placeholder={t('machines.detail.customMaintenance.descriptionPlaceholder')}
            />
          </div>
        </div>

        {/* AGC: Footer */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={hours <= 0 || isSaving}
            className="flex-1 py-2.5 px-4 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {t('common.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomMaintenanceModal
